import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Music, Users, Theater, TrendingUp, Info, RefreshCw } from 'lucide-react';
import recommendationService from '../../../services/recommendationService';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';

const PersonalizedRecommendations = ({ userId }) => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('songs'); // 'songs', 'roles', 'musicals'
  
  const [recommendations, setRecommendations] = useState({
    songs: { recommendations: [], reasoning: '', generatedWithAI: false },
    roles: { recommendations: [], reasoning: '', generatedWithAI: false },
    musicals: { recommendations: [], reasoning: '', generatedWithAI: false }
  });

  useEffect(() => {
    if (userId) {
      loadAllRecommendations();
    }
  }, [userId]);

  const loadAllRecommendations = async () => {
    try {
      setLoading(true);
      setError('');

      // Load all types of recommendations in parallel
      const [songsData, rolesData, musicalsData] = await Promise.all([
        recommendationService?.getPersonalizedSongRecommendations(userId, 4),
        recommendationService?.getPersonalizedRoleRecommendations(userId, 4),
        recommendationService?.getPersonalizedMusicalRecommendations(userId, 4)
      ]);

      setRecommendations({
        songs: songsData,
        roles: rolesData,
        musicals: musicalsData
      });
    } catch (err) {
      console.error('Error loading recommendations:', err);
      setError('Failed to load recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllRecommendations();
    setRefreshing(false);
  };

  const getMatchColor = (score) => {
    if (score >= 0.8) return 'text-green-400 bg-green-400/20';
    if (score >= 0.6) return 'text-yellow-400 bg-yellow-400/20';
    return 'text-orange-400 bg-orange-400/20';
  };

  const getMatchLabel = (score) => {
    if (score >= 0.8) return 'Excellent Match';
    if (score >= 0.6) return 'Good Match';
    return 'Worth Exploring';
  };

  const navigateToSong = (songId, title) => {
    // Find song by title since recommendations might not always have IDs
    navigate(`/song-detail-page?search=${encodeURIComponent(title)}`);
  };

  const navigateToRole = (roleId, characterName) => {
    navigate(`/role-detail-page?search=${encodeURIComponent(characterName)}`);
  };

  const navigateToMusical = (musicalId, title) => {
    navigate(`/musical-progress-page?search=${encodeURIComponent(title)}`);
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-purple-600/20 to-indigo-600/20 backdrop-blur-md rounded-2xl p-6 border border-purple-400/30">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-3 text-white">Generating personalized recommendations...</span>
        </div>
      </div>
    );
  }

  const currentRecommendations = recommendations?.[activeTab];
  const hasRecommendations = currentRecommendations?.recommendations?.length > 0;

  return (
    <div className="bg-gradient-to-br from-purple-600/20 to-indigo-600/20 backdrop-blur-md rounded-2xl p-6 border border-purple-400/30">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">AI-Powered Recommendations</h2>
            <p className="text-purple-200 text-sm">
              {currentRecommendations?.generatedWithAI ? (
                <span className="flex items-center gap-1">
                  <span className="inline-block w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  Powered by OpenAI
                </span>
              ) : (
                'Based on your learning patterns'
              )}
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
          title="Refresh recommendations"
        >
          <RefreshCw className={`w-5 h-5 text-white ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 bg-red-500/10 border border-red-500 rounded-xl p-3">
          <p className="text-red-200 text-sm">{error}</p>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 bg-white/5 rounded-xl p-1">
        <button
          onClick={() => setActiveTab('songs')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'songs' ?'bg-purple-600 text-white shadow-lg' : 'text-white/70 hover:text-white hover:bg-white/5'
          }`}
        >
          <Music className="w-4 h-4" />
          <span className="hidden sm:inline">Songs</span>
        </button>
        <button
          onClick={() => setActiveTab('roles')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'roles' ?'bg-purple-600 text-white shadow-lg' : 'text-white/70 hover:text-white hover:bg-white/5'
          }`}
        >
          <Users className="w-4 h-4" />
          <span className="hidden sm:inline">Roles</span>
        </button>
        <button
          onClick={() => setActiveTab('musicals')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'musicals' ?'bg-purple-600 text-white shadow-lg' : 'text-white/70 hover:text-white hover:bg-white/5'
          }`}
        >
          <Theater className="w-4 h-4" />
          <span className="hidden sm:inline">Musicals</span>
        </button>
      </div>

      {/* AI Reasoning */}
      {currentRecommendations?.reasoning && (
        <div className="mb-6 bg-white/5 rounded-xl p-4 border border-white/10">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-purple-300 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-purple-200 font-medium text-sm mb-1">Why these recommendations?</p>
              <p className="text-white/70 text-sm">{currentRecommendations?.reasoning}</p>
            </div>
          </div>
        </div>
      )}

      {/* Recommendations Grid */}
      {hasRecommendations ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currentRecommendations?.recommendations?.map((item, index) => (
            <div
              key={index}
              className="bg-white/10 rounded-xl p-4 hover:bg-white/15 transition-all cursor-pointer group"
              onClick={() => {
                if (activeTab === 'songs') {
                  navigateToSong(item?.id, item?.title);
                } else if (activeTab === 'roles') {
                  navigateToRole(item?.id, item?.characterName);
                } else {
                  navigateToMusical(item?.id, item?.title);
                }
              }}
            >
              {/* Header with Match Score */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-white font-semibold text-lg group-hover:text-purple-300 transition-colors">
                    {activeTab === 'songs' && item?.title}
                    {activeTab === 'roles' && item?.characterName}
                    {activeTab === 'musicals' && item?.title}
                  </h3>
                  <p className="text-purple-300 text-sm">
                    {activeTab === 'songs' && `from ${item?.musical}`}
                    {activeTab === 'roles' && `${item?.voiceType} • ${item?.musical}`}
                    {activeTab === 'musicals' && item?.genre?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                  </p>
                </div>
                <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${getMatchColor(item?.confidenceScore)}`}>
                  <TrendingUp className="w-3 h-3" />
                  <span>{Math.round(item?.confidenceScore * 100)}%</span>
                </div>
              </div>

              {/* Match Label */}
              <div className="mb-3">
                <span className="inline-block px-3 py-1 bg-purple-500/30 text-purple-200 rounded-lg text-xs font-medium">
                  {getMatchLabel(item?.confidenceScore)}
                </span>
              </div>

              {/* Recommendation Reason */}
              <div className="bg-white/5 rounded-lg p-3 mb-3">
                <p className="text-white/80 text-sm leading-relaxed">
                  {item?.reason}
                </p>
              </div>

              {/* Additional Context */}
              {activeTab === 'songs' && item?.skillMatch && (
                <div className="flex items-center gap-2 text-xs text-white/60">
                  <span className="capitalize">{item?.skillMatch} Level</span>
                </div>
              )}
              {activeTab === 'roles' && (
                <div className="flex flex-wrap gap-2">
                  {item?.vocalRange && (
                    <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">
                      {item?.vocalRange}
                    </span>
                  )}
                  {item?.skillLevel && (
                    <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded text-xs capitalize">
                      {item?.skillLevel}
                    </span>
                  )}
                </div>
              )}
              {activeTab === 'musicals' && item?.explorationValue && (
                <p className="text-white/60 text-xs italic">{item?.explorationValue}</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Sparkles className="w-12 h-12 text-purple-400 mx-auto mb-3" />
          <p className="text-white/70 text-lg mb-2">No recommendations available yet</p>
          <p className="text-white/50 text-sm">
            Complete more lessons to unlock personalized AI recommendations
          </p>
        </div>
      )}
    </div>
  );
};

export default PersonalizedRecommendations;